import type { RefetchQueriesInclude } from '@apollo/client';
import type {
  GetFinderItemsQueryVariables,
  GetShareAssetsQueryVariables,
  MetadataGroupByInput,
} from '@frameio/graphql-client/generated/';
import {
  GetFinderItems,
  GetShareAssets,
} from '@frameio/graphql-client/generated/';
import type { FinderRefetchOptions } from '@frameio/shared/contexts/FinderRefetchContext';
import { FINDER_CURSOR_PAGE_SIZE } from '@frameio/shared/finder/constants';
import { getIsOrderPossiblyChanged } from './getIsOrderPossiblyChanged';
import {
  type GetFinderItemsObservableQuery,
  type GetShareAssetsObservableQuery,
  isQueryGetFinderItems,
  isQueryGetShareAssets,
} from './shared';

/**
 * getRefetchFinderItemsOptions
 *
 * This utility function returns refetch query options for Finder 1.1 assets.
 */
export const getRefetchFinderItemsOptions = (
  observedQueryNames: Set<string>,
  opts?: FinderRefetchOptions
) => {
  const include: RefetchQueriesInclude = [];
  observedQueryNames.forEach((queryName) => {
    if (queryName === 'GetFinderItems') {
      include.push(GetFinderItems);
    }
    if (queryName === 'GetShareAssets') {
      include.push(GetShareAssets);
    }
  });
  return {
    include,
    onQueryUpdated: (
      query: GetFinderItemsObservableQuery | GetShareAssetsObservableQuery
    ) => {
      if (
        opts?.changedFieldDefinitionIds &&
        !getIsOrderPossiblyChanged(query, opts.changedFieldDefinitionIds)
      ) {
        return false;
      }

      let first: number = FINDER_CURSOR_PAGE_SIZE;

      /** set the requested count to match the total number of nodes we've already fetched */
      if (isQueryGetFinderItems(query)) {
        const asset = query.getCurrentResult()?.data?.account?.asset;

        /** bail if onlyRefetchForGroups is true and there are no groups */
        if (
          opts?.onlyRefetchForGroups &&
          (!query.variables?.groupBys ||
            !(query.variables?.groupBys as Array<MetadataGroupByInput>).length)
        ) {
          return false;
        }

        first =
          asset?.__typename === 'FolderAsset' // it's always a FolderAsset, just getting types to work
            ? asset?.matchingChildren?.nodes?.length ?? 0
            : 0;
      } else if (isQueryGetShareAssets(query)) {
        const share = query.getCurrentResult()?.data?.share;

        first = share?.assets?.nodes?.length ?? 0;
      }

      /** round up to the nearest page size */
      first =
        first === 0
          ? FINDER_CURSOR_PAGE_SIZE
          : Math.ceil(first / FINDER_CURSOR_PAGE_SIZE) *
            FINDER_CURSOR_PAGE_SIZE;

      query.setVariables({
        ...(query.variables as GetFinderItemsQueryVariables &
          GetShareAssetsQueryVariables),
        page: {
          first,
        },
      });

      return true;
    },
  };
};
